import type { GameplayAnalyticsEnvelope } from '@brick-rogue/analytics-contracts';
import type {
  ActiveAbilityId,
  BuildId,
  GameplayAnalyticsEvent,
  RiskContractId,
  RunCurseId,
  RunEventId,
} from '@brick-rogue/game-core';

import { GAME_PLATFORM } from './platform/gamePlatform';

export const GAMEPLAY_ANALYTICS_EVENT_NAME = 'brick-rogue:gameplay-analytics';
export const GAMEPLAY_BASELINE_STORAGE_KEY = 'brick-rogue:gameplay-baseline:v6';
const PREVIOUS_GAMEPLAY_BASELINE_STORAGE_KEY = 'brick-rogue:gameplay-baseline:v5';
const CLEANSE_GAMEPLAY_BASELINE_STORAGE_KEY = 'brick-rogue:gameplay-baseline:v4';
const LEGACY_GAMEPLAY_BASELINE_STORAGE_KEY = 'brick-rogue:gameplay-baseline:v3';
const EVENT_ROOM_GAMEPLAY_BASELINE_STORAGE_KEY = 'brick-rogue:gameplay-baseline:v2';
const ORIGINAL_GAMEPLAY_BASELINE_STORAGE_KEY = 'brick-rogue:gameplay-baseline:v1';

type CursedRelicId = 'cursed-reactor';

interface CurseLifecycleBaseline {
  readonly activated: number;
  readonly cleansed: number;
  readonly expired: number;
}

interface CleanseLifecycleBaseline {
  readonly offered: number;
  readonly cleansed: number;
  readonly retained: number;
}

interface CursedRelicLifecycleBaseline {
  readonly obtained: number;
  readonly activated: number;
  readonly removedByCleanse: number;
  readonly secured: number;
}

interface BuildChallengeLifecycleBaseline {
  readonly started: number;
  readonly completed: number;
  readonly lost: number;
  readonly restarted: number;
  readonly exited: number;
}

const endpoint = import.meta.env.VITE_GAMEPLAY_ANALYTICS_ENDPOINT?.trim();
const buildId = typeof __APP_BUILD_ID__ === 'string' ? __APP_BUILD_ID__ : 'test';

export interface WaveBaseline {
  readonly starts: number;
  readonly completions: number;
  readonly totalDurationMilliseconds: number;
  readonly totalScoreGained: number;
  readonly totalBricksDestroyed: number;
  readonly totalLivesRemaining: number;
  readonly peakProjectiles: number;
}

export interface GameplayBaseline {
  readonly schemaVersion: 6;
  readonly firstOccurredAt: string | null;
  readonly lastOccurredAt: string | null;
  readonly collectedEvents: number;
  readonly runs: Readonly<{
    started: number;
    won: number;
    lost: number;
    restarted: number;
    exited: number;
    totalScore: number;
    totalElapsedSeconds: number;
  }>;
  readonly rewards: Readonly<{
    offered: number;
    exhausted: number;
    selected: number;
    rerolled: number;
    skipped: number;
    autoRecovered: number;
  }>;
  readonly contracts: Readonly<{
    offered: number;
    accepted: number;
    declined: number;
    acceptedById: Readonly<Record<RiskContractId, number>>;
  }>;
  readonly runEvents: Readonly<{
    offered: number;
    affordable: number;
    accepted: number;
    declined: number;
    acceptedById: Readonly<Record<RunEventId, number>>;
    totalScoreSpent: number;
    totalScoreGained: number;
    totalLivesSpent: number;
    totalLivesRepaired: number;
  }>;
  readonly runCurses: Readonly<
    CurseLifecycleBaseline & {
      readonly byId: Readonly<Record<RunCurseId, CurseLifecycleBaseline>>;
    }
  >;
  readonly cleanseNodes: Readonly<
    CleanseLifecycleBaseline & {
      readonly byCurseId: Readonly<Record<RunCurseId, CleanseLifecycleBaseline>>;
    }
  >;
  readonly cursedRelics: Readonly<
    CursedRelicLifecycleBaseline & {
      readonly byId: Readonly<Record<CursedRelicId, CursedRelicLifecycleBaseline>>;
    }
  >;
  readonly buildChallenges: Readonly<
    BuildChallengeLifecycleBaseline & {
      readonly byBuild: Readonly<Record<BuildId, BuildChallengeLifecycleBaseline>>;
    }
  >;
  readonly bossMechanics: Readonly<{
    shieldBroken: number;
    shieldRestored: number;
    reinforcementsSummoned: number;
    reinforcementsDestroyed: number;
    singularityWarned: number;
    singularityActivated: number;
    singularityEnded: number;
  }>;
  readonly builds: Readonly<Record<BuildId, number>>;
  readonly abilities: Readonly<Record<ActiveAbilityId, number>>;
  readonly waves: Readonly<Record<string, WaveBaseline>>;
}

const sessionIdsByRunId = new Map<string, string>();

export function reportGameplayEvent(
  event: GameplayAnalyticsEvent,
): GameplayAnalyticsEnvelope | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const envelope = createGameplayAnalyticsEnvelope(event);

  persistGameplayBaseline(envelope);
  window.dispatchEvent(new CustomEvent(GAMEPLAY_ANALYTICS_EVENT_NAME, { detail: envelope }));
  if (!endpoint) {
    return envelope;
  }

  const body = JSON.stringify(envelope);
  try {
    if (navigator.sendBeacon?.(endpoint, new Blob([body], { type: 'application/json' }))) {
      return envelope;
    }
  } catch {
    // Analytics transport is deliberately isolated from the game loop.
  }
  void fetch(endpoint, {
    method: 'POST',
    body,
    headers: { 'content-type': 'application/json' },
    keepalive: true,
  }).catch(() => undefined);
  return envelope;
}

export function createGameplayAnalyticsEnvelope(
  event: GameplayAnalyticsEvent,
  options: Readonly<{
    eventId?: string;
    sessionId?: string;
    occurredAt?: string;
    inputMode?: GameplayAnalyticsEnvelope['inputMode'];
  }> = {},
): GameplayAnalyticsEnvelope {
  const sessionId = options.sessionId ?? resolveSessionId(event.runId);
  const envelope: GameplayAnalyticsEnvelope = {
    schemaVersion: 6,
    rulesVersion: 1,
    eventId: options.eventId ?? createUuid(),
    sessionId,
    buildId,
    platform: GAME_PLATFORM,
    inputMode:
      options.inputMode ??
      (typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches
        ? 'coarse'
        : 'fine'),
    occurredAt: options.occurredAt ?? new Date().toISOString(),
    event,
  };
  if (event.type === 'run-ended') {
    sessionIdsByRunId.delete(event.runId);
  }
  return envelope;
}

export function createEmptyGameplayBaseline(): GameplayBaseline {
  return {
    schemaVersion: 6,
    firstOccurredAt: null,
    lastOccurredAt: null,
    collectedEvents: 0,
    runs: {
      started: 0,
      won: 0,
      lost: 0,
      restarted: 0,
      exited: 0,
      totalScore: 0,
      totalElapsedSeconds: 0,
    },
    rewards: {
      offered: 0,
      exhausted: 0,
      selected: 0,
      rerolled: 0,
      skipped: 0,
      autoRecovered: 0,
    },
    contracts: {
      offered: 0,
      accepted: 0,
      declined: 0,
      acceptedById: { 'siege-bounty': 0, 'redline-license': 0, 'null-dividend': 0 },
    },
    runEvents: {
      offered: 0,
      affordable: 0,
      accepted: 0,
      declined: 0,
      acceptedById: {
        'nanite-repair': 0,
        'vitality-exchange': 0,
        'echo-forge': 0,
        'glass-overclock': 0,
      },
      totalScoreSpent: 0,
      totalScoreGained: 0,
      totalLivesSpent: 0,
      totalLivesRepaired: 0,
    },
    runCurses: {
      activated: 0,
      cleansed: 0,
      expired: 0,
      byId: {
        'glass-overclock': { activated: 0, cleansed: 0, expired: 0 },
        'reactor-instability': { activated: 0, cleansed: 0, expired: 0 },
      },
    },
    cleanseNodes: {
      offered: 0,
      cleansed: 0,
      retained: 0,
      byCurseId: {
        'glass-overclock': { offered: 0, cleansed: 0, retained: 0 },
        'reactor-instability': { offered: 0, cleansed: 0, retained: 0 },
      },
    },
    cursedRelics: {
      obtained: 0,
      activated: 0,
      removedByCleanse: 0,
      secured: 0,
      byId: {
        'cursed-reactor': { obtained: 0, activated: 0, removedByCleanse: 0, secured: 0 },
      },
    },
    buildChallenges: {
      started: 0,
      completed: 0,
      lost: 0,
      restarted: 0,
      exited: 0,
      byBuild: {
        swarm: createEmptyBuildChallengeLifecycle(),
        burst: createEmptyBuildChallengeLifecycle(),
        fortress: createEmptyBuildChallengeLifecycle(),
      },
    },
    bossMechanics: {
      shieldBroken: 0,
      shieldRestored: 0,
      reinforcementsSummoned: 0,
      reinforcementsDestroyed: 0,
      singularityWarned: 0,
      singularityActivated: 0,
      singularityEnded: 0,
    },
    builds: { swarm: 0, burst: 0, fortress: 0 },
    abilities: { 'nova-split': 0, 'aegis-field': 0, 'ion-pulse': 0 },
    waves: {},
  };
}

export function readGameplayBaseline(): GameplayBaseline {
  if (typeof window === 'undefined') {
    return createEmptyGameplayBaseline();
  }
  try {
    const stored =
      localStorage.getItem(GAMEPLAY_BASELINE_STORAGE_KEY) ??
      localStorage.getItem(PREVIOUS_GAMEPLAY_BASELINE_STORAGE_KEY) ??
      localStorage.getItem(CLEANSE_GAMEPLAY_BASELINE_STORAGE_KEY) ??
      localStorage.getItem(LEGACY_GAMEPLAY_BASELINE_STORAGE_KEY) ??
      localStorage.getItem(EVENT_ROOM_GAMEPLAY_BASELINE_STORAGE_KEY) ??
      localStorage.getItem(ORIGINAL_GAMEPLAY_BASELINE_STORAGE_KEY);
    return normalizeGameplayBaseline(stored ? JSON.parse(stored) : null);
  } catch {
    return createEmptyGameplayBaseline();
  }
}

export function normalizeGameplayBaseline(value: unknown): GameplayBaseline {
  const empty = createEmptyGameplayBaseline();
  if (!isRecord(value) || ![1, 2, 3, 4, 5, 6].includes(Number(value.schemaVersion))) {
    return empty;
  }
  const sourceSchemaVersion = Number(value.schemaVersion);
  const runs = isRecord(value.runs) ? value.runs : {};
  const rewards = isRecord(value.rewards) ? value.rewards : {};
  const contracts = isRecord(value.contracts) ? value.contracts : {};
  const runEvents = isRecord(value.runEvents) ? value.runEvents : {};
  const runCurses = isRecord(value.runCurses) ? value.runCurses : {};
  const cleanseNodes = isRecord(value.cleanseNodes) ? value.cleanseNodes : {};
  const cursedRelics = isRecord(value.cursedRelics) ? value.cursedRelics : {};
  const buildChallenges = isRecord(value.buildChallenges) ? value.buildChallenges : {};
  const buildChallengesByBuild = isRecord(buildChallenges.byBuild) ? buildChallenges.byBuild : {};
  const runCursesById = isRecord(runCurses.byId) ? runCurses.byId : {};
  const cleanseNodesByCurseId = isRecord(cleanseNodes.byCurseId) ? cleanseNodes.byCurseId : {};
  const cursedRelicsById = isRecord(cursedRelics.byId) ? cursedRelics.byId : {};
  const glassCurse = isRecord(runCursesById['glass-overclock'])
    ? runCursesById['glass-overclock']
    : {};
  const reactorCurse = isRecord(runCursesById['reactor-instability'])
    ? runCursesById['reactor-instability']
    : {};
  const glassCleanse = isRecord(cleanseNodesByCurseId['glass-overclock'])
    ? cleanseNodesByCurseId['glass-overclock']
    : {};
  const reactorCleanse = isRecord(cleanseNodesByCurseId['reactor-instability'])
    ? cleanseNodesByCurseId['reactor-instability']
    : {};
  const cursedReactor = isRecord(cursedRelicsById['cursed-reactor'])
    ? cursedRelicsById['cursed-reactor']
    : {};
  const bossMechanics = isRecord(value.bossMechanics) ? value.bossMechanics : {};
  const acceptedContracts = isRecord(contracts.acceptedById) ? contracts.acceptedById : {};
  const acceptedRunEvents = isRecord(runEvents.acceptedById) ? runEvents.acceptedById : {};
  const builds = isRecord(value.builds) ? value.builds : {};
  const abilities = isRecord(value.abilities) ? value.abilities : {};
  const waveEntries = isRecord(value.waves)
    ? Object.entries(value.waves).flatMap(([wave, candidate]) => {
        if (!/^([1-9]|[12]\d|30)$/.test(wave) || !isRecord(candidate)) {
          return [];
        }
        return [
          [
            wave,
            {
              starts: toCount(candidate.starts),
              completions: toCount(candidate.completions),
              totalDurationMilliseconds: toCount(candidate.totalDurationMilliseconds),
              totalScoreGained: toCount(candidate.totalScoreGained),
              totalBricksDestroyed: toCount(candidate.totalBricksDestroyed),
              totalLivesRemaining: toCount(candidate.totalLivesRemaining),
              peakProjectiles: toCount(candidate.peakProjectiles),
            },
          ] as const,
        ];
      })
    : [];

  return {
    schemaVersion: 6,
    firstOccurredAt: typeof value.firstOccurredAt === 'string' ? value.firstOccurredAt : null,
    lastOccurredAt: typeof value.lastOccurredAt === 'string' ? value.lastOccurredAt : null,
    collectedEvents: toCount(value.collectedEvents),
    runs: {
      started: toCount(runs.started),
      won: toCount(runs.won),
      lost: toCount(runs.lost),
      restarted: toCount(runs.restarted),
      exited: toCount(runs.exited),
      totalScore: toCount(runs.totalScore),
      totalElapsedSeconds: toCount(runs.totalElapsedSeconds),
    },
    rewards: {
      offered: toCount(rewards.offered),
      exhausted: toCount(rewards.exhausted),
      selected: toCount(rewards.selected),
      rerolled: toCount(rewards.rerolled),
      skipped: toCount(rewards.skipped),
      autoRecovered: toCount(rewards.autoRecovered),
    },
    contracts: {
      offered: toCount(contracts.offered),
      accepted: toCount(contracts.accepted),
      declined: toCount(contracts.declined),
      acceptedById: {
        'siege-bounty': toCount(acceptedContracts['siege-bounty']),
        'redline-license': toCount(acceptedContracts['redline-license']),
        'null-dividend': toCount(acceptedContracts['null-dividend']),
      },
    },
    runEvents: {
      offered: toCount(runEvents.offered),
      affordable: toCount(runEvents.affordable),
      accepted: toCount(runEvents.accepted),
      declined: toCount(runEvents.declined),
      acceptedById: {
        'nanite-repair': toCount(acceptedRunEvents['nanite-repair']),
        'vitality-exchange': toCount(acceptedRunEvents['vitality-exchange']),
        'echo-forge': toCount(acceptedRunEvents['echo-forge']),
        'glass-overclock': toCount(acceptedRunEvents['glass-overclock']),
      },
      totalScoreSpent: toCount(runEvents.totalScoreSpent),
      totalScoreGained: toCount(runEvents.totalScoreGained),
      totalLivesSpent: toCount(runEvents.totalLivesSpent),
      totalLivesRepaired: toCount(runEvents.totalLivesRepaired),
    },
    runCurses: {
      activated: toCount(runCurses.activated),
      cleansed: toCount(runCurses.cleansed),
      expired: toCount(runCurses.expired),
      byId: {
        'glass-overclock': {
          activated: toCount(sourceSchemaVersion < 5 ? runCurses.activated : glassCurse.activated),
          cleansed: toCount(sourceSchemaVersion < 5 ? runCurses.cleansed : glassCurse.cleansed),
          expired: toCount(sourceSchemaVersion < 5 ? runCurses.expired : glassCurse.expired),
        },
        'reactor-instability': {
          activated: toCount(reactorCurse.activated),
          cleansed: toCount(reactorCurse.cleansed),
          expired: toCount(reactorCurse.expired),
        },
      },
    },
    cleanseNodes: {
      offered: toCount(cleanseNodes.offered),
      cleansed: toCount(cleanseNodes.cleansed),
      retained: toCount(cleanseNodes.retained),
      byCurseId: {
        'glass-overclock': {
          offered: toCount(sourceSchemaVersion < 5 ? cleanseNodes.offered : glassCleanse.offered),
          cleansed: toCount(
            sourceSchemaVersion < 5 ? cleanseNodes.cleansed : glassCleanse.cleansed,
          ),
          retained: toCount(
            sourceSchemaVersion < 5 ? cleanseNodes.retained : glassCleanse.retained,
          ),
        },
        'reactor-instability': {
          offered: toCount(reactorCleanse.offered),
          cleansed: toCount(reactorCleanse.cleansed),
          retained: toCount(reactorCleanse.retained),
        },
      },
    },
    cursedRelics: {
      obtained: toCount(cursedRelics.obtained),
      activated: toCount(cursedRelics.activated),
      removedByCleanse: toCount(cursedRelics.removedByCleanse),
      secured: toCount(cursedRelics.secured),
      byId: {
        'cursed-reactor': {
          obtained: toCount(cursedReactor.obtained),
          activated: toCount(cursedReactor.activated),
          removedByCleanse: toCount(cursedReactor.removedByCleanse),
          secured: toCount(cursedReactor.secured),
        },
      },
    },
    buildChallenges: {
      started: toCount(buildChallenges.started),
      completed: toCount(buildChallenges.completed),
      lost: toCount(buildChallenges.lost),
      restarted: toCount(buildChallenges.restarted),
      exited: toCount(buildChallenges.exited),
      byBuild: {
        swarm: normalizeBuildChallengeLifecycle(buildChallengesByBuild.swarm),
        burst: normalizeBuildChallengeLifecycle(buildChallengesByBuild.burst),
        fortress: normalizeBuildChallengeLifecycle(buildChallengesByBuild.fortress),
      },
    },
    bossMechanics: {
      shieldBroken: toCount(bossMechanics.shieldBroken),
      shieldRestored: toCount(bossMechanics.shieldRestored),
      reinforcementsSummoned: toCount(bossMechanics.reinforcementsSummoned),
      reinforcementsDestroyed: toCount(bossMechanics.reinforcementsDestroyed),
      singularityWarned: toCount(bossMechanics.singularityWarned),
      singularityActivated: toCount(bossMechanics.singularityActivated),
      singularityEnded: toCount(bossMechanics.singularityEnded),
    },
    builds: {
      swarm: toCount(builds.swarm),
      burst: toCount(builds.burst),
      fortress: toCount(builds.fortress),
    },
    abilities: {
      'nova-split': toCount(abilities['nova-split']),
      'aegis-field': toCount(abilities['aegis-field']),
      'ion-pulse': toCount(abilities['ion-pulse']),
    },
    waves: Object.fromEntries(waveEntries),
  };
}

export function updateGameplayBaseline(
  baseline: GameplayBaseline,
  event: GameplayAnalyticsEvent,
  occurredAt: string,
): GameplayBaseline {
  if (event.challengeId === 'build-mastery') {
    let buildChallenges = baseline.buildChallenges;
    if (event.type === 'run-started') {
      const build = buildChallenges.byBuild[event.buildId];
      buildChallenges = {
        ...buildChallenges,
        started: buildChallenges.started + 1,
        byBuild: {
          ...buildChallenges.byBuild,
          [event.buildId]: { ...build, started: build.started + 1 },
        },
      };
    } else if (event.type === 'run-ended') {
      const key = event.challengeCompleted || event.reason === 'won' ? 'completed' : event.reason;
      const build = buildChallenges.byBuild[event.buildId];
      buildChallenges = {
        ...buildChallenges,
        [key]: buildChallenges[key] + 1,
        byBuild: {
          ...buildChallenges.byBuild,
          [event.buildId]: { ...build, [key]: build[key] + 1 },
        },
      };
    }
    return {
      ...baseline,
      firstOccurredAt: baseline.firstOccurredAt ?? occurredAt,
      lastOccurredAt: occurredAt,
      collectedEvents: baseline.collectedEvents + 1,
      buildChallenges,
    };
  }

  let runs = baseline.runs;
  let rewards = baseline.rewards;
  let contracts = baseline.contracts;
  let runEvents = baseline.runEvents;
  let runCurses = baseline.runCurses;
  let cleanseNodes = baseline.cleanseNodes;
  let cursedRelics = baseline.cursedRelics;
  let bossMechanics = baseline.bossMechanics;
  let builds = baseline.builds;
  let abilities = baseline.abilities;
  let waves = baseline.waves;

  switch (event.type) {
    case 'run-started':
      runs = { ...runs, started: runs.started + 1 };
      builds = { ...builds, [event.buildId]: builds[event.buildId] + 1 };
      break;
    case 'wave-started': {
      const wave = resolveWaveBaseline(waves, event.wave);
      waves = { ...waves, [event.wave]: { ...wave, starts: wave.starts + 1 } };
      break;
    }
    case 'wave-completed': {
      const wave = resolveWaveBaseline(waves, event.wave);
      waves = {
        ...waves,
        [event.wave]: {
          ...wave,
          completions: wave.completions + 1,
          totalDurationMilliseconds: wave.totalDurationMilliseconds + event.durationMilliseconds,
          totalScoreGained: wave.totalScoreGained + event.scoreGained,
          totalBricksDestroyed: wave.totalBricksDestroyed + event.bricksDestroyed,
          totalLivesRemaining: wave.totalLivesRemaining + event.livesRemaining,
          peakProjectiles: Math.max(wave.peakProjectiles, event.peakProjectiles),
        },
      };
      break;
    }
    case 'reward-offered':
      rewards = {
        ...rewards,
        offered: rewards.offered + 1,
        exhausted: rewards.exhausted + Number(event.exhausted),
      };
      break;
    case 'reward-action': {
      const key =
        event.action === 'auto-recovered'
          ? 'autoRecovered'
          : event.action === 'selected'
            ? 'selected'
            : event.action === 'rerolled'
              ? 'rerolled'
              : 'skipped';
      rewards = { ...rewards, [key]: rewards[key] + 1 };
      if (
        event.action === 'selected' &&
        event.rewardKind === 'relic' &&
        event.selectedId === 'cursed-reactor'
      ) {
        const relic = cursedRelics.byId['cursed-reactor'];
        cursedRelics = {
          ...cursedRelics,
          obtained: cursedRelics.obtained + 1,
          byId: {
            ...cursedRelics.byId,
            'cursed-reactor': { ...relic, obtained: relic.obtained + 1 },
          },
        };
      }
      break;
    }
    case 'contract-offered':
      contracts = { ...contracts, offered: contracts.offered + 1 };
      break;
    case 'contract-action':
      contracts =
        event.action === 'accepted'
          ? {
              ...contracts,
              accepted: contracts.accepted + 1,
              acceptedById: {
                ...contracts.acceptedById,
                [event.contractId]: contracts.acceptedById[event.contractId] + 1,
              },
            }
          : { ...contracts, declined: contracts.declined + 1 };
      break;
    case 'run-event-offered':
      runEvents = {
        ...runEvents,
        offered: runEvents.offered + 1,
        affordable: runEvents.affordable + Number(event.affordable),
      };
      break;
    case 'run-event-action':
      runEvents =
        event.action === 'accepted'
          ? {
              ...runEvents,
              accepted: runEvents.accepted + 1,
              acceptedById: {
                ...runEvents.acceptedById,
                [event.runEventId]: runEvents.acceptedById[event.runEventId] + 1,
              },
              totalScoreSpent:
                runEvents.totalScoreSpent + Math.max(0, event.scoreBefore - event.scoreAfter),
              totalScoreGained:
                runEvents.totalScoreGained + Math.max(0, event.scoreAfter - event.scoreBefore),
              totalLivesSpent:
                runEvents.totalLivesSpent + Math.max(0, event.livesBefore - event.livesAfter),
              totalLivesRepaired:
                runEvents.totalLivesRepaired + Math.max(0, event.livesAfter - event.livesBefore),
            }
          : { ...runEvents, declined: runEvents.declined + 1 };
      break;
    case 'run-curse-status':
      runCurses = {
        ...runCurses,
        [event.action]: runCurses[event.action] + 1,
        byId: {
          ...runCurses.byId,
          [event.curseId]: {
            ...runCurses.byId[event.curseId],
            [event.action]: runCurses.byId[event.curseId][event.action] + 1,
          },
        },
      };
      if (event.source === 'relic' && event.sourceRelicId === 'cursed-reactor') {
        const key =
          event.action === 'activated'
            ? 'activated'
            : event.action === 'cleansed'
              ? 'removedByCleanse'
              : 'secured';
        const relic = cursedRelics.byId['cursed-reactor'];
        cursedRelics = {
          ...cursedRelics,
          [key]: cursedRelics[key] + 1,
          byId: {
            ...cursedRelics.byId,
            'cursed-reactor': { ...relic, [key]: relic[key] + 1 },
          },
        };
      }
      break;
    case 'run-cleanse-offered':
      cleanseNodes = {
        ...cleanseNodes,
        offered: cleanseNodes.offered + 1,
        byCurseId: {
          ...cleanseNodes.byCurseId,
          [event.curseId]: {
            ...cleanseNodes.byCurseId[event.curseId],
            offered: cleanseNodes.byCurseId[event.curseId].offered + 1,
          },
        },
      };
      break;
    case 'run-cleanse-action':
      cleanseNodes = {
        ...cleanseNodes,
        [event.action]: cleanseNodes[event.action] + 1,
        byCurseId: {
          ...cleanseNodes.byCurseId,
          [event.curseId]: {
            ...cleanseNodes.byCurseId[event.curseId],
            [event.action]: cleanseNodes.byCurseId[event.curseId][event.action] + 1,
          },
        },
      };
      break;
    case 'ability-used':
      abilities = { ...abilities, [event.abilityId]: abilities[event.abilityId] + 1 };
      break;
    case 'boss-mechanic':
      if (event.action === 'shield-broken') {
        bossMechanics = { ...bossMechanics, shieldBroken: bossMechanics.shieldBroken + 1 };
      } else if (event.action === 'shield-restored') {
        bossMechanics = { ...bossMechanics, shieldRestored: bossMechanics.shieldRestored + 1 };
      } else if (event.action === 'reinforcement-summoned') {
        bossMechanics = {
          ...bossMechanics,
          reinforcementsSummoned: bossMechanics.reinforcementsSummoned + 1,
        };
      } else if (event.action === 'reinforcement-destroyed') {
        bossMechanics = {
          ...bossMechanics,
          reinforcementsDestroyed: bossMechanics.reinforcementsDestroyed + 1,
        };
      } else {
        const key =
          event.action === 'field-warned'
            ? 'singularityWarned'
            : event.action === 'field-activated'
              ? 'singularityActivated'
              : 'singularityEnded';
        bossMechanics = { ...bossMechanics, [key]: bossMechanics[key] + 1 };
      }
      break;
    case 'run-ended':
      runs = {
        ...runs,
        [event.reason]: runs[event.reason] + 1,
        totalScore: runs.totalScore + event.score,
        totalElapsedSeconds: runs.totalElapsedSeconds + event.stats.elapsedSeconds,
      };
      break;
    case 'build-selected':
    case 'life-lost':
      break;
  }

  return {
    ...baseline,
    firstOccurredAt: baseline.firstOccurredAt ?? occurredAt,
    lastOccurredAt: occurredAt,
    collectedEvents: baseline.collectedEvents + 1,
    runs,
    rewards,
    contracts,
    runEvents,
    runCurses,
    cleanseNodes,
    cursedRelics,
    bossMechanics,
    builds,
    abilities,
    waves,
  };
}

function persistGameplayBaseline(envelope: GameplayAnalyticsEnvelope): void {
  try {
    const baseline = readGameplayBaseline();
    localStorage.setItem(
      GAMEPLAY_BASELINE_STORAGE_KEY,
      JSON.stringify(updateGameplayBaseline(baseline, envelope.event, envelope.occurredAt)),
    );
  } catch {
    // Storage limits and privacy modes must never interrupt gameplay or remote reporting.
  }
}

function resolveSessionId(runId: string): string {
  const existingSessionId = sessionIdsByRunId.get(runId);
  if (existingSessionId) {
    return existingSessionId;
  }
  const sessionId = createUuid();
  sessionIdsByRunId.set(runId, sessionId);
  return sessionId;
}

function createUuid(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hexadecimal = [...bytes].map((value) => value.toString(16).padStart(2, '0')).join('');
  return [
    hexadecimal.slice(0, 8),
    hexadecimal.slice(8, 12),
    hexadecimal.slice(12, 16),
    hexadecimal.slice(16, 20),
    hexadecimal.slice(20),
  ].join('-');
}

function resolveWaveBaseline(waves: GameplayBaseline['waves'], wave: number): WaveBaseline {
  return (
    waves[wave] ?? {
      starts: 0,
      completions: 0,
      totalDurationMilliseconds: 0,
      totalScoreGained: 0,
      totalBricksDestroyed: 0,
      totalLivesRemaining: 0,
      peakProjectiles: 0,
    }
  );
}

function createEmptyBuildChallengeLifecycle(): BuildChallengeLifecycleBaseline {
  return { started: 0, completed: 0, lost: 0, restarted: 0, exited: 0 };
}

function normalizeBuildChallengeLifecycle(value: unknown): BuildChallengeLifecycleBaseline {
  const source = isRecord(value) ? value : {};
  return {
    started: toCount(source.started),
    completed: toCount(source.completed),
    lost: toCount(source.lost),
    restarted: toCount(source.restarted),
    exited: toCount(source.exited),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toCount(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.max(0, value) : 0;
}
